import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Modal from "../common/components/ThemedModal";
import { BrandTeam, Rider } from "../../types/model";
import { getBrandTeamRiders } from "../../services/brandTeamService";

interface BrandTeamProfileModalProps {
	show: boolean;
	onHide: () => void;
	team: BrandTeam;
	sportId?: string;
	seriesId?: string;
}

const BrandTeamProfileModal: React.FC<BrandTeamProfileModalProps> = ({
	show,
	onHide,
	team,
	sportId,
	seriesId,
}) => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [riders, setRiders] = useState<Rider[]>([]);

	useEffect(() => {
		const fetchTeamRiders = async () => {
			if (!show || !team?._id) {
				return;
			}

			try {
				setLoading(true);
				setError("");
				const res = await getBrandTeamRiders(team._id, {
					sportId,
					seriesId,
					status: "active",
				});
				setRiders(Array.isArray(res?.data) ? res.data : []);
			} catch (fetchError) {
				console.error("Failed to fetch brand team riders:", fetchError);
				setError("Failed to load team roster.");
				setRiders([]);
			} finally {
				setLoading(false);
			}
		};

		void fetchTeamRiders();
	}, [show, team?._id, sportId, seriesId]);

	const teamHistorySummaryByRider = useMemo(() => {
		const teamId = team?._id ? String(team._id) : "";
		const map = new Map<string, string>();

		riders.forEach((rider) => {
			const entries = Array.isArray((rider as any)?.teamHistory)
				? (rider as any).teamHistory
				: [];

			const seasons = entries
				.filter((entry: any) => {
					const entryTeamId =
						typeof entry?.team === "string" ? entry.team : entry?.team?._id;
					return teamId && String(entryTeamId || "") === teamId;
				})
				.map((entry: any) => {
					if (typeof entry?.season === "string") {
						return entry.season;
					}
					return entry?.season?.name || "";
				})
				.filter(Boolean);

			const uniqueSeasons = Array.from(new Set(seasons));
			map.set(rider._id || `${rider.first_name}-${rider.last_name}`, uniqueSeasons.join(", "));
		});

		return map;
	}, [riders, team?._id]);

	return (
		<Modal show={show} onHide={onHide} size="lg">
			<Modal.Header closeButton>
				<Modal.Title>{team?.name || "Team"} Roster</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
						<div className="d-flex align-items-center gap-3">
							{team?.imageUrl ? (
							<img
								src={team.imageUrl}
								alt={team?.name || "Team"}
								style={{
									width: "64px",
									height: "64px",
									objectFit: "contain",
									borderRadius: "10px",
								}}
							/>
							) : null}
							<div>
								<div className="fw-bold fs-5">{team?.name}</div>
							</div>
						</div>
					<Badge bg="secondary" className="fs-6">
						{riders.length} athlete{riders.length === 1 ? "" : "s"}
					</Badge>
				</div>

				{loading ? (
					<div className="d-flex align-items-center gap-2">
						<Spinner animation="border" size="sm" />
						<span>Loading team athletes...</span>
					</div>
				) : error ? (
					<Alert variant="danger" className="mb-0">
						{error}
					</Alert>
				) : riders.length === 0 ? (
					<Alert variant="secondary" className="mb-0">
						No athletes found for this team in the selected context.
					</Alert>
				) : (
						<Table responsive striped bordered hover variant="dark">
						<thead>
							<tr>
								<th>Athlete</th>
								<th>Country</th>
								<th>Team History</th>
								<th className="text-nowrap">Profile</th>
							</tr>
						</thead>
						<tbody>
							{riders.map((rider) => {
								const riderId = rider._id || "";
								const key = riderId || `${rider.first_name}-${rider.last_name}`;
								return (
									<tr key={key}>
										<td>
											{rider.first_name} {rider.last_name}
										</td>
										<td>{rider.country || "-"}</td>
										<td>{teamHistorySummaryByRider.get(key) || "-"}</td>
										<td className="text-nowrap">
											<Button
												variant="outline-light"
												size="sm"
												disabled={!riderId}
												onClick={() => {
													if (!riderId) return;
													onHide();
													navigate(`/riders/${riderId}`);
												}}
											>
												Athlete Profile
											</Button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				)}
			</Modal.Body>
		</Modal>
	);
};

export default BrandTeamProfileModal;
